'use client';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'ar' | 'fr' | 'es' | 'hi' | 'ml';

export interface LanguageOption {
  code: Language;
  label: string;
  nativeLabel: string;
  dir: 'ltr' | 'rtl';
  flag: string;
}

export const LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', dir: 'ltr', flag: '🇬🇧' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', dir: 'rtl', flag: '🇸🇦' },
  { code: 'fr', label: 'French', nativeLabel: 'Français', dir: 'ltr', flag: '🇫🇷' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español', dir: 'ltr', flag: '🇪🇸' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी', dir: 'ltr', flag: '🇮🇳' },
  { code: 'ml', label: 'Malayalam', nativeLabel: 'മലയാളം', dir: 'ltr', flag: '🇮🇳' },
];

type Translations = Record<string, string>;

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  dir: 'ltr' | 'rtl';
  currentLang: LanguageOption;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

const translationCache: Partial<Record<Language, Translations>> = {};

async function loadTranslations(lang: Language): Promise<Translations> {
  if (translationCache[lang]) return translationCache[lang]!;
  try {
    const mod = await import(`@/locales/${lang}.json`);
    translationCache[lang] = mod.default as Translations;
    return translationCache[lang]!;
  } catch {
    return {};
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>('en');
  const [translations, setTranslations] = useState<Translations>({});

  useEffect(() => {
    const saved = (typeof window !== 'undefined' ? localStorage.getItem('tota_lang') : null) as Language | null;
    const initial: Language = saved && LANGUAGES.find((l) => l.code === saved) ? saved : 'en';
    setLanguageState(initial);
    loadTranslations(initial).then(setTranslations);
  }, []);

  useEffect(() => {
    const lang = LANGUAGES.find((l) => l.code === language)!;
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
      document.documentElement.dir = lang.dir;
    }
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== 'undefined') localStorage.setItem('tota_lang', lang);
    loadTranslations(lang).then(setTranslations);
  };

  const t = (key: string): string => translations[key] ?? key;

  const currentLang = LANGUAGES.find((l) => l.code === language)!;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dir: currentLang.dir, currentLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
